import { QueryBuilderQuery, QueryBuilderRule } from './advancedSearchApiTypes';
import {
  ArrowType,
  BooleanOperator,
  DirectedTriple,
  DirectedTripleWithFilters,
  LineType,
  LocalFilterId,
  PathResult,
} from './apiTypes';
import { ArdoqId } from './coreTypes';
import { APIScopeDataWithExplicitParentChildReferences } from './scopeDataAPITypes';

/**
 * A single path defined as a sequence of triples.
 */
export type Path = DirectedTripleWithFilters[];

export enum TraversalPathMatchingType {
  LOOSE = 'loose', // default, no strict matching
  STRICT = 'strict', // strict matching on all paths, with an OR-operator
  SUPERSTRICT = 'superstrict', // strict matching on all paths, with an AND-operator
}

export type TraversalBase = {
  paths: Path[];
  filters: ViewpointBuilderFilters;
  pathMatching: TraversalPathMatchingType;
  pathCollapsingRules: PathCollapsingRule[];
  /**
   * When set, the resolved start set is expanded with all of its descendants in
   * the component hierarchy before the traversal runs. See {@link StartSetExpansion}.
   */
  startSetExpansion?: StartSetExpansion;
  /**
   * The component type the expansion started from. Only meaningful together with
   * {@link startSetExpansion}, and set at the same place - a traversal over a
   * hierarchy of several component types stores `anyType` as the start endpoint
   * of its paths, so the type it expanded from is not recoverable from them.
   *
   * The backend ignores it; it resolves the start set from `componentSelection`
   * and expands from there.
   */
  startSetExpansionFromType?: string;
  /**
   * Set on a traversal the presentation build converted from a workspace-mode
   * slide. Views draw it the way workspace mode drew the slide.
   */
  convertedFrom?: 'workspaceMode';
};

export type TraversalParams = TraversalBase & {
  componentSelection: ComponentSelection;
};

/**
 * @deprecated Poorly named, use TraversalParams instead.
 */
export type Traversal = TraversalParams;

/**
 * The payload POSTed to `/api/traversal/execute` to run a traversal.
 */
export type APILoadTraversalParams = {
  /** Explicit list of component ids for start context; empty when `startQuery` is used instead. */
  startSet: ArdoqId[];
  paths: DirectedTripleWithFilters[][];
  startQuery: QueryBuilderQuery | null;
  filters: ViewpointBuilderFilters;
  pathMatching: TraversalPathMatchingType;
  pathCollapsingRules: PathCollapsingRule[];
  includeAttachments?: boolean;
  startSetExpansion?: StartSetExpansion;
};

/**
 * Describes how the start context of a traversal (or search) is selected.
 *
 * - `startSet`: an explicit, manually selected list of component ids.
 * - `startQuery`: a query whose matches resolve to the start context
 *   ("select all" matching the query) — e.g. all components of a given type.
 *
 * The two may coexist. When a traversal is executed, `startSet` is used as the
 * start context if it is non-empty; otherwise we fall back to `startQuery`.
 * Keeping both populated lets us preserve the originating query when the user
 * narrows it down to a manual `startSet`, so the query isn't lost if they
 * widen the selection again. Use {@link hasStartSet} / {@link hasStartQuery}
 * to branch on which is present rather than inspecting the fields directly.
 */
export type ComponentSelection = {
  startSet: string[];
  startQuery?: QueryBuilderQuery;
};

export const hasStartSet = (componentSelection: ComponentSelection): boolean =>
  componentSelection.startSet.length > 0;

export const hasStartQuery = (
  componentSelection: ComponentSelection
): componentSelection is ComponentSelection & {
  startQuery: QueryBuilderQuery;
} => Boolean(componentSelection.startQuery);

export const getContextComponentIds = (
  componentSelection: ComponentSelection,
  startSetResult: ArdoqId[]
): ArdoqId[] =>
  hasStartSet(componentSelection)
    ? componentSelection.startSet
    : startSetResult;

/**
 * Maps traversal params (as held in a traversal loaded state) into the payload
 * POSTed to `/api/traversal/execute`. Dispatch rule: use the `startSet` if it is
 * non-empty, otherwise fall back to the `startQuery`.
 *
 * `includeAttachments` is intentionally left unset: it is not part of the traversal
 * params held in view state, so the resulting payload always omits it.
 */
export const traversalParamsToLoadTraversalParams = ({
  componentSelection,
  paths,
  filters,
  pathMatching,
  pathCollapsingRules,
  startSetExpansion,
}: TraversalParams): APILoadTraversalParams => {
  const shouldUseStartSet = hasStartSet(componentSelection);
  return {
    startSet: shouldUseStartSet ? componentSelection.startSet : [],
    startQuery: shouldUseStartSet
      ? null
      : (componentSelection.startQuery ?? null),
    paths: toRequestedPaths(paths, startSetExpansion),
    filters,
    pathMatching,
    pathCollapsingRules: pathCollapsingRules ?? [],
    ...(startSetExpansion ? { startSetExpansion } : {}),
  };
};

/**
 * Optional start-set expansion strategy for a traversal. When set, the resolved
 * start set is expanded before the traversal runs. Modelled as an enumerated
 * string (rather than a boolean) to mirror the backend contract and leave room
 * for future strategies (e.g. 'ancestors', 'subtree').
 */
export type StartSetExpansion = 'descendants';

/**
 * The backend matches requested paths to returned `pathResults` by index, so an
 * expansion with no paths must request a single empty path for the expanded
 * start set to come back as `pathResults[0]`. The stored representation keeps
 * `paths` empty, hence the translation on the way to a request.
 */
export const toRequestedPaths = (
  paths: DirectedTripleWithFilters[][],
  startSetExpansion: StartSetExpansion | null | undefined
): DirectedTripleWithFilters[][] =>
  startSetExpansion && paths.length === 0 ? [[]] : paths;

export type APICountInstancesResponse = {
  traversalCounts: {
    path: DirectedTriple[];
    counts: number[];
  }[];
  /**
   * The size of the originally resolved start set, filtered to those reachable
   * under strict path matching. NOT expanded with descendants.
   */
  startSetCount: number;
  /**
   * The size of the (possibly expanded) start set used as the traversal's start
   * vertices: the expanded subtree when `startSetExpansion` is set, otherwise
   * equal to `startSetCount`. This is the meaningful "start node" count in the
   * viewpoint builder, including when there are no traversal paths yet.
   */
  expandedStartSetCount: number;
};

export type ApiCountInstancesRequest = {
  startSet: string[] | null;
  startQuery: QueryBuilderQuery | null;
  paths: DirectedTriple[][];
  filters: ViewpointBuilderFilters | null;
  startSetFilterId: string | null;
  pathMatching: TraversalPathMatchingType;
  startSetExpansion?: StartSetExpansion;
};

export type ViewpointBuilderFilterPerNode = {
  filterId: LocalFilterId;
  /**
   * No nesting of filters, just one level deep.
   * Backend accepts the same format as advanced search.
   */
  rules: (Omit<QueryBuilderRule, 'value'> & {
    // TODO try to fix in the API types
    value: string | number | boolean | null;
  })[];
  condition: BooleanOperator;
};

export type ViewpointBuilderFilters = Record<
  LocalFilterId,
  ViewpointBuilderFilterPerNode
>;

type ReferenceTypeStyle = {
  color: string;
  line: LineType;
  lineBeginning: ArrowType;
  lineEnding: ArrowType;
  svgStyle: string | null;
};

/**
 * A rule with this set also collapses the hierarchy hops a start-set expansion
 * prepends to the traversal, so the connections of the descendants are allocated
 * to an ancestor rather than staying on the descendant itself.
 */
export type PathCollapsingRecursion = {
  /** Where the hops the rule absorbs come from. */
  strategy: 'startSetExpansionDescendants';
  /**
   * How many hierarchy levels stay as they are. `0` collapses the whole
   * hierarchy onto the root; `1` keeps the root's children, and the connections
   * of anything below them are allocated to those children.
   */
  keepExpandedLevels: number;
};

export type PathCollapsingRule = {
  referenceTypeId: string;
  path: DirectedTriple[];
  displayText: string;
  /**
   * For the future, for now hard coded values.
   */
  referenceStyle: ReferenceTypeStyle;
  isActive: boolean;
  collapseRecursively?: PathCollapsingRecursion;
};

export type PathCollapsingRuleNonNullablePath = PathCollapsingRule & {
  path: DirectedTriple[];
};

export type APILoadTraversalResponse = {
  scopeData: APIScopeDataWithExplicitParentChildReferences;
  startSetResult: string[];
  pathResults: PathResult[];
};

export type PathMatch = {
  startIndex: number;
  length: number;
  start: 'start' | 'end';
  rulePathKey: string;
  matchCount: number;
};

export type CollapsingPathRuleMatch<T extends DirectedTripleWithFilters> = {
  path: T[];
  match: PathMatch;
  rule: PathCollapsingRuleNonNullablePath;
  pathIndex: number;
  matchKey: string;
};
